import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { gengql } from '@/__generated__';
import { notify } from '@/actions/ui';
import { CognitoTotpEnrolment } from '@/auth/components/Login/CognitoTotpEnrolment';
import {
  SOCIAL_TWO_FACTOR_NOTE,
  isSocialSignInEnabled,
} from '@/auth/components/SocialSignInButtons/SocialSignInButtons';
import { disableOtpAuth } from '@/auth/store';
import { toUnauthorized } from '@/auth/utils/cognitoLogin';
import {
  type CognitoMfaState,
  disableMfaFactor,
  fetchCognitoMfaState,
} from '@/auth/utils/cognitoMfa';
import {
  type CognitoMfaType,
  SIGN_IN_EXPIRED_MESSAGE,
  type TotpSetup,
  completeTotpEnrolment,
  startTotpEnrolment,
} from '@/auth/utils/cognitoSignIn';
import SettingsSection from '@/components/SettingsSection';
import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';
import { useAppDispatch } from '@/store';

import CognitoEmailEnrolment from './CognitoEmailEnrolment';
import CognitoMfaDisableForm from './CognitoMfaDisableForm';
import CognitoMfaFactorList, { factorPhrase } from './CognitoMfaFactorList';
import CognitoMfaSwitchForm from './CognitoMfaSwitchForm';
import CognitoSmsEnrolment from './CognitoSmsEnrolment';
import { MfaSettingsError, mfaErrorMessage } from './mfaErrorMessage';

const GET_TEAM_MFA_POLICY = gengql(/* GraphQL */ `
  query cognitoMfaSettingsTeamPolicy($userId: String!) {
    user(id: $userId) {
      id
      groups {
        id
        enforce_2fa
      }
    }
  }
`);

const TEAM_REQUIRES_FACTOR =
  'Your team requires two-factor authentication. Set up another method before turning this one off.';
const CHANGE_IN_PROGRESS = 'Another two-factor change is still in progress.';
const FACTOR_REMOVAL_FAILED =
  'Two-factor authentication was turned off on the platform, but the method could not be ' +
  'removed. Please try again.';

type OpenDialog = { action: 'switch' | 'set_up' | 'turn_off'; type: CognitoMfaType } | null;

const DIALOG_VERB = { switch: 'Switch to', set_up: 'Set up', turn_off: 'Turn off' } as const;

interface CognitoMfaSettingsProps {
  /** The signed-in user: Cognito MFA settings only ever apply to the viewer. */
  userId: string;
}

/**
 * Two-factor settings on Cognito (ENG-6107), shown instead of the legacy
 * dialogs while a Cognito session is active. Cognito holds the factors; the
 * api keeps the team policy (`enforce_2fa`) and its own MFA flag, which is
 * cleared when the last factor goes so its gate does not ask for one again.
 */
export default function CognitoMfaSettings({ userId }: CognitoMfaSettingsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<CognitoMfaState>();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [busy, setBusy] = useState(false);
  // The last read of the factors failed: what is on screen may be out of date.
  const [stale, setStale] = useState(false);
  // One change at a time, from the first request (a password check, a code
  // verification) until it settles. While held, the dialog cannot be dismissed
  // and nothing new can start, so a late answer always lands in the dialog
  // that asked for it and two removals never decide on the same state.
  const lock = useRef(false);
  const [changing, setChanging] = useState(false);
  const policy = useQuery(GET_TEAM_MFA_POLICY, { variables: { userId } });
  // Until the team policy is known, nothing may change: an unknown policy must
  // not let the last factor go.
  const policyReady = !policy.loading && !policy.error && !!policy.data;
  const teamEnforced = !!policy.data?.user?.groups?.some((group) => group.enforce_2fa);

  // Controls stay off until the factors are fresh; a failed refresh keeps them
  // off rather than let the user act on a list that may be stale, and offers
  // to try again.
  const reload = useCallback(async () => {
    setBusy(true);
    try {
      setState(await fetchCognitoMfaState());
      setStale(false);
    } catch (err) {
      setStale(true);
      dispatch(notify(mfaErrorMessage(err), 'error'));
    }
    setBusy(false);
  }, [dispatch]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // App setup holds the lock from the secret request until it settles, so the
  // dialog cannot be closed and reopened into a second association whose late
  // answer would replace the secret on screen. A repeated start (StrictMode
  // runs effects twice) shares the pending request instead of being refused.
  const totpStart = useRef<Promise<TotpSetup> | null>(null);
  const email = state?.email;
  const startTotp = useCallback((): Promise<TotpSetup> => {
    if (totpStart.current) return totpStart.current;
    if (lock.current) return Promise.reject(new MfaSettingsError(CHANGE_IN_PROGRESS));
    lock.current = true;
    setChanging(true);
    totpStart.current = startTotpEnrolment(email)
      .catch((err: unknown) => Promise.reject(toUnauthorized(err)))
      .finally(() => {
        totpStart.current = null;
        lock.current = false;
        setChanging(false);
      });
    return totpStart.current;
  }, [email]);

  const runExclusive = useCallback(async <T,>(op: () => Promise<T>): Promise<T> => {
    if (lock.current) throw new MfaSettingsError(CHANGE_IN_PROGRESS);
    lock.current = true;
    setChanging(true);
    try {
      return await op();
    } finally {
      lock.current = false;
      setChanging(false);
    }
  }, []);

  // The enrolment challenge lapsed: close it; the user can start again. The
  // code may have been verified before the preference change lapsed, so the
  // factors are read again.
  const handleExpired = useCallback(() => {
    setDialog(null);
    dispatch(notify(SIGN_IN_EXPIRED_MESSAGE, 'error'));
    void reload();
  }, [dispatch, reload]);

  async function finish(message: string) {
    setDialog(null);
    dispatch(notify(message));
    await reload();
  }

  // One method at a time (ENG-6323): turning one on turns the others off in
  // the same preference update, after the password is checked as for any
  // turn-off. Two on at once (set up before this) is cleaned up by the next
  // one turned on.
  const othersOn = (type: CognitoMfaType): CognitoMfaType[] =>
    state?.enabled.filter((t) => t !== type) ?? [];

  function handleSetUp(type: CognitoMfaType) {
    if (othersOn(type).length) setDialog({ action: 'switch', type });
    else setUp(type);
  }

  // Every method goes on only after a code is entered (ENG-6323).
  function setUp(type: CognitoMfaType) {
    setDialog({ action: 'set_up', type });
  }

  // Turns a factor off after the password check. Rejects, with nothing
  // changed, back to the form; resolves once something did change.
  async function disableFactor(type: CognitoMfaType): Promise<void> {
    setBusy(true);
    try {
      await removeFactorApiFirst(type);
    } catch (err) {
      setBusy(false);
      throw err;
    }
  }

  // Decides on fresh data, never on the list on screen. When the last factor
  // goes, the api's MFA flag is cleared first: while a factor exists its gate
  // is open, and with none left a set flag would gate the very mutation that
  // clears it. A Cognito failure after that leaves a factor: the safe side.
  async function removeFactorApiFirst(type: CognitoMfaType): Promise<void> {
    const [current, latestPolicy] = await Promise.all([fetchCognitoMfaState(), policy.refetch()]);
    setState(current);
    if (!current.enabled.includes(type)) {
      await finish('That method is already off.');
      return;
    }
    const remaining = current.enabled.filter((t) => t !== type);
    const enforced = !!latestPolicy.data?.user?.groups?.some((group) => group.enforce_2fa);
    if (!remaining.length && enforced) throw new MfaSettingsError(TEAM_REQUIRES_FACTOR);
    if (!remaining.length) await dispatch(disableOtpAuth(userId));
    try {
      await disableMfaFactor(type, current);
    } catch (err) {
      if (remaining.length) throw err;
      Sentry.captureException(err);
      setDialog(null);
      dispatch(notify(FACTOR_REMOVAL_FAILED, 'error'));
      await reload();
      return;
    }
    await finish('Two-factor method turned off.');
  }

  function closeDialog() {
    if (lock.current) return;
    setDialog(null);
    // A dismissed setup may have changed Cognito (a new phone number
    // confirmed or awaiting its code), so the next dialog reads fresh.
    void reload();
  }

  function dialogBody(open: NonNullable<OpenDialog>, current: CognitoMfaState): JSX.Element {
    if (open.action === 'switch') {
      return (
        <CognitoMfaSwitchForm
          type={open.type}
          turnOff={othersOn(open.type)}
          email={current.email}
          onConfirmed={() => {
            setUp(open.type);
          }}
          onCancel={closeDialog}
          runExclusive={runExclusive}
        />
      );
    }
    if (open.action === 'turn_off') {
      return (
        <CognitoMfaDisableForm
          type={open.type}
          email={current.email}
          onConfirmed={() => disableFactor(open.type)}
          onCancel={closeDialog}
          runExclusive={runExclusive}
        />
      );
    }
    if (open.type === 'EMAIL') {
      return (
        <CognitoEmailEnrolment
          turnOff={othersOn('EMAIL')}
          onEnrolled={() => void finish('Email codes are now on for your account.')}
          onCancel={closeDialog}
          runExclusive={runExclusive}
        />
      );
    }
    if (open.type === 'SMS') {
      return (
        <CognitoSmsEnrolment
          userId={userId}
          turnOff={othersOn('SMS')}
          onEnrolled={() => void finish('Text message codes are now on for your account.')}
          onCancel={closeDialog}
          runExclusive={runExclusive}
        />
      );
    }
    return (
      <CognitoTotpEnrolment
        start={startTotp}
        verify={(code) =>
          runExclusive(() =>
            completeTotpEnrolment(code, othersOn('TOTP')).catch((err: unknown) =>
              Promise.reject(toUnauthorized(err))
            )
          )
        }
        onEnrolled={() => void finish('Authentication app codes are now on for your account.')}
        onExpired={handleExpired}
        onCancel={closeDialog}
      />
    );
  }

  const socialNote = isSocialSignInEnabled() && (
    <p className="mb-12 text-on-surface-secondary">{SOCIAL_TWO_FACTOR_NOTE}</p>
  );

  const retry = stale && (
    <div className="mb-12 flex flex-wrap items-center gap-12">
      <p className="text-on-surface-secondary">We could not load your two-factor methods.</p>
      <Button
        className="min-h-48 min-w-48"
        variant="secondary"
        size="small"
        disabled={busy}
        onClick={() => void reload()}
      >
        Try again
      </Button>
    </div>
  );

  if (!state) {
    return (
      <SettingsSection box title="Two-factor verification">
        {socialNote}
        {retry || <p className="text-on-surface-secondary">Loading…</p>}
      </SettingsSection>
    );
  }

  return (
    <SettingsSection box title="Two-factor verification">
      {socialNote}
      {policy.error && (
        <p className="mb-12 text-on-surface-secondary">
          We could not load your team&apos;s two-factor policy. Reload the page to change your
          methods.
        </p>
      )}
      {retry}
      <CognitoMfaFactorList
        enabled={state.enabled}
        teamEnforced={teamEnforced}
        busy={busy || stale || changing || !policyReady}
        onSetUp={handleSetUp}
        onTurnOff={(type) => setDialog({ action: 'turn_off', type })}
      />
      {dialog && (
        <Dialog
          open
          onClose={closeDialog}
          dialogTitle={`${DIALOG_VERB[dialog.action]} ${factorPhrase(dialog.type)}`}
          // Hidden while a change is in flight, so a late answer lands in its own dialog.
          showCloseButton={!changing}
          // The X sits at the end of the title row; a long title wraps rather than push it out.
          className="[&_.d-dialog-header>*]:shrink-0 [&_.d-dialog-header>h2]:min-w-0 [&_.d-dialog-header>h2]:shrink [&_.d-dialog-header]:flex [&_.d-dialog-header]:items-center [&_.d-dialog-header]:justify-between [&_.d-dialog-header]:pr-12"
          // Portalled, so the rest of the page is hidden from assistive tech and the dialog is not.
          disablePortal={false}
        >
          {dialogBody(dialog, state)}
        </Dialog>
      )}
    </SettingsSection>
  );
}
